import { useCallback, useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { ResponsiveImg } from "@/components/responsive-img";
import { imgSizes } from "@/lib/images";
import { Reveal } from "@/components/reveal";
import { useFocusTrap } from "@/hooks/use-focus-trap";

export type GalleryItem = { image: string; caption: string; meta?: string };

export function AnimatedGallery({ items }: { items: GalleryItem[] }) {
  const [open, setOpen] = useState<number | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(null), []);

  useFocusTrap(open !== null, dialogRef, close);

  useEffect(() => {
    if (open === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") setOpen((i) => ((i ?? 0) + 1) % items.length);
      if (e.key === "ArrowLeft") setOpen((i) => ((i ?? 0) - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, items.length]);

  return (
    <>
      <div className="columns-1 gap-6 sm:columns-2 lg:columns-3 [&>*]:mb-6">
        {items.map((item, i) => (
          <Reveal key={item.image + i} delay={(i % 3) * 90} className="break-inside-avoid">
            <button
              type="button"
              onClick={() => setOpen(i)}
              className="plate group block w-full text-left"
              aria-label={`View ${item.caption}`}
            >
              <ResponsiveImg
                name={item.image}
                sizes={imgSizes.third}
                alt={item.caption}
                loading="lazy"
                decoding="async"
                className="w-full object-cover"
              />
              <span className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-3 bg-[linear-gradient(to_top,var(--ink),transparent)] p-5 pt-14 text-[11px] uppercase tracking-[0.22em] text-[oklch(0.96_0.008_240)] opacity-0 transition-all duration-700 group-hover:translate-y-0 group-hover:opacity-100">
                {item.caption}
                {item.meta ? (
                  <span className="ml-2 text-[var(--amber-warm)]">· {item.meta}</span>
                ) : null}
              </span>
            </button>
          </Reveal>
        ))}
      </div>

      {open !== null ? (
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={items[open]!.caption}
          tabIndex={-1}
          className="fixed inset-0 z-[80] flex animate-fade-blur items-center justify-center bg-[oklch(0.19_0.055_262_/_92%)] p-4 backdrop-blur-xl"
          onClick={close}
        >
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="absolute right-5 top-5 grid size-11 place-items-center rounded-full border border-white/25 text-[oklch(0.96_0.008_240)] transition-colors hover:border-[var(--amber-warm)] hover:text-[var(--amber-warm)]"
          >
            <X className="size-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpen((i) => ((i ?? 0) - 1 + items.length) % items.length);
            }}
            aria-label="Previous"
            className="absolute left-4 grid size-11 place-items-center rounded-full border border-white/25 text-[oklch(0.96_0.008_240)] transition-colors hover:border-[var(--amber-warm)] hover:text-[var(--amber-warm)] md:left-10"
          >
            <ChevronLeft className="size-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpen((i) => ((i ?? 0) + 1) % items.length);
            }}
            aria-label="Next"
            className="absolute right-4 grid size-11 place-items-center rounded-full border border-white/25 text-[oklch(0.96_0.008_240)] transition-colors hover:border-[var(--amber-warm)] hover:text-[var(--amber-warm)] md:right-10"
          >
            <ChevronRight className="size-4" aria-hidden />
          </button>
          <figure
            key={open}
            className="animate-rise max-h-[86vh] max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            <ResponsiveImg
              name={items[open]!.image}
              sizes={imgSizes.lightbox}
              alt={items[open]!.caption}
              className="max-h-[76vh] w-auto max-w-full rounded-lg object-contain shadow-[var(--shadow-lift)]"
            />
            <figcaption className="mt-4 text-center text-[11px] uppercase tracking-[0.26em] text-[oklch(0.88_0.02_240)]">
              {items[open]!.caption}
              {items[open]!.meta ? (
                <span className="ml-2 text-[var(--amber-warm)]">· {items[open]!.meta}</span>
              ) : null}
            </figcaption>
          </figure>
        </div>
      ) : null}
    </>
  );
}
